// DuckDB Worker Proxy
//
// メインスレッドからWorkerへの通信を管理し、
// キャンセル機能を提供する。

import type { DataSet } from '../../../types/dataset.types';
import type { DataSchema } from '../../../types/core.types';
import type { OperationResult } from '../executeOperation';
import type { DuckDBWorkerRequest, DuckDBWorkerResponse } from './types';
import type { TableReference } from './index';
import { DuckDBCancelledError } from './types';
import logger from '../logger';

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

/**
 * DuckDB Workerへのプロキシ
 *
 * メインスレッドからWorkerへの通信を管理し、
 * キャンセル機能（Worker.terminate()）を提供する。
 */
export class DuckDBWorkerProxy {
  private worker: Worker | null = null;
  private pendingRequests: Map<number, PendingRequest> = new Map();
  private requestId = 0;
  private initPromise: Promise<void> | null = null;
  private isInitializing = false;

  /**
   * Workerを初期化
   */
  async init(): Promise<void> {
    // 既に初期化中または初期化済みの場合は既存のPromiseを返す
    if (this.initPromise) {
      return this.initPromise;
    }

    this.isInitializing = true;
    this.initPromise = this.doInit();

    try {
      await this.initPromise;
    } catch (error) {
      // 初期化失敗時はリセット
      this.initPromise = null;
      this.isInitializing = false;
      throw error;
    }

    this.isInitializing = false;
  }

  private async doInit(): Promise<void> {
    logger.info('[DuckDBProxy] Initializing worker');

    // Workerを作成（標準的なESModules構文を使用）
    this.worker = new Worker(
      new URL('../../workers/duckdb.worker.ts', import.meta.url),
      { type: 'module' }
    );

    // メッセージハンドラを設定
    this.worker.onmessage = (event: MessageEvent<DuckDBWorkerResponse>) => {
      this.handleMessage(event.data);
    };

    this.worker.onerror = (event) => {
      logger.error('[DuckDBProxy] Worker error', { error: event.message });
      // 全てのpending requestsをreject
      for (const [, { reject }] of this.pendingRequests) {
        reject(new Error(`Worker error: ${event.message}`));
      }
      this.pendingRequests.clear();
    };

    // Worker内のDuckDBを初期化
    await this.sendRequest({ type: 'init', id: this.getNextId() });

    logger.info('[DuckDBProxy] Worker initialized');
  }

  /**
   * SQLクエリを実行
   */
  async executeSQL(
    query: string,
    targetDataset: DataSet,
    datasets: Record<string, DataSet>,
    existingSchema?: DataSchema
  ): Promise<OperationResult> {
    await this.ensureInitialized();

    const result = await this.sendRequest({
      type: 'executeSQL',
      id: this.getNextId(),
      query,
      targetDataset,
      datasets,
      existingSchema,
    });

    return result as OperationResult;
  }

  /**
   * 全テーブルをクリア
   */
  async clearAllTables(): Promise<void> {
    await this.ensureInitialized();

    await this.sendRequest({
      type: 'clearAllTables',
      id: this.getNextId(),
    });
  }

  /**
   * SQLクエリから参照されているテーブル名と位置情報を抽出
   */
  async extractTableReferencesWithLocation(query: string): Promise<TableReference[]> {
    await this.ensureInitialized();

    const result = await this.sendRequest({
      type: 'extractTableReferencesWithLocation',
      id: this.getNextId(),
      query,
    });

    return result as TableReference[];
  }

  /**
   * 現在の操作をキャンセルし、Workerを再起動
   */
  async cancelAndReinitialize(): Promise<void> {
    logger.info('[DuckDBProxy] Cancelling current operation');

    if (this.worker) {
      // Workerを終了
      this.worker.terminate();
      this.worker = null;
    }

    // 全てのpending requestsをreject
    for (const [id, { reject }] of this.pendingRequests) {
      logger.debug('[DuckDBProxy] Rejecting pending request', { id });
      reject(new DuckDBCancelledError());
    }
    this.pendingRequests.clear();

    // 初期化状態をリセット
    this.initPromise = null;
    this.isInitializing = false;

    // 新しいWorkerを起動して初期化
    logger.info('[DuckDBProxy] Reinitializing worker');
    await this.init();

    logger.info('[DuckDBProxy] Worker reinitialized');
  }

  /**
   * 現在実行中の操作があるかどうか
   */
  isExecuting(): boolean {
    return this.pendingRequests.size > 0;
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.worker && !this.isInitializing) {
      await this.init();
    } else if (this.initPromise) {
      await this.initPromise;
    }
  }

  private getNextId(): number {
    return ++this.requestId;
  }

  private sendRequest(request: DuckDBWorkerRequest): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error('Worker not initialized'));
        return;
      }

      this.pendingRequests.set(request.id, { resolve, reject });
      this.worker.postMessage(request);
    });
  }

  private handleMessage(response: DuckDBWorkerResponse): void {
    const pending = this.pendingRequests.get(response.id);
    if (!pending) {
      logger.warn('[DuckDBProxy] Received response for unknown request', { id: response.id });
      return;
    }

    this.pendingRequests.delete(response.id);

    switch (response.type) {
      case 'ready':
      case 'cleared':
        pending.resolve(undefined);
        break;

      case 'result':
        pending.resolve(response.result);
        break;

      case 'tableReferences':
        pending.resolve(response.references);
        break;

      case 'error':
        pending.reject(new Error(response.error));
        break;
    }
  }
}

// グローバルインスタンス
export const duckdbProxy = new DuckDBWorkerProxy();

// E2Eテスト用: windowオブジェクトに公開
// DuckDBプロキシをグローバルに公開（E2Eテストで使用）
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__MIDAS_DUCKDB_PROXY__ = duckdbProxy;
}
